import { useCallback, useEffect, useState } from 'react';

/**
 * useCart — minimal cart state, localStorage-backed.
 *
 * Today: single global cart per browser, persisted to localStorage so a tab
 * reload doesn't drop items. Map of catalogItemId → qty (positive ints).
 *
 * Next session (per CLAUDE.md): scope the key by (customer, location) and
 * mirror to `orders.order_drafts` via Supabase so AP users have per-location
 * carts across devices. Server wins on conflict by `last_modified_at`.
 */

const STORAGE_KEY = 'brix-order:cart:v1';

export type CartState = Record<string, number>;

function readFromStorage(): CartState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: CartState = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
          out[k] = Math.floor(v);
        }
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}

export interface UseCart {
  cart: CartState;
  setQty: (catalogItemId: string, qty: number) => void;
  adjustQty: (catalogItemId: string, delta: number) => void;
  remove: (catalogItemId: string) => void;
  clear: () => void;
  count: number;
}

export function useCart(): UseCart {
  const [cart, setCart] = useState<CartState>(() => readFromStorage());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {
      /* quota / private mode — silently ignore. */
    }
  }, [cart]);

  const setQty = useCallback((id: string, qty: number) => {
    setCart((prev) => {
      const next = { ...prev };
      const clamped = Math.max(0, Math.floor(qty));
      if (clamped === 0) delete next[id];
      else next[id] = clamped;
      return next;
    });
  }, []);

  const adjustQty = useCallback((id: string, delta: number) => {
    setCart((prev) => {
      const next = { ...prev };
      const current = next[id] ?? 0;
      const updated = Math.max(0, current + delta);
      if (updated === 0) delete next[id];
      else next[id] = updated;
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setCart((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const clear = useCallback(() => setCart({}), []);

  const count = Object.values(cart).reduce((s, n) => s + n, 0);

  return { cart, setQty, adjustQty, remove, clear, count };
}
